import { z } from 'zod';
import { ProductTaxClassSchema } from './types';

/**
 * Canada sales-tax reference table, one row per province and territory.
 *
 * Federal: 5% GST everywhere except the HST provinces, where a single
 * harmonised rate replaces GST + provincial tax (ON 13%, NS 14% since
 * 2025-04-01, NB/NL/PE 15%). Source: CRA "GST/HST rates and place-of-supply
 * rules" (modified 2026-07-15). Provincial retail taxes stack on top of the
 * 5% GST in BC (7% PST), SK (6% PST), MB (7% RST) and QC (9.975% QST).
 *
 * Place of supply for goods sent by mail or courier is the province they are
 * delivered to (GST/HST Memorandum 3-3-3), so the destination row is the
 * only one that matters.
 *
 * The rule that decides everything for Sellwild: a marketplace ("distribution
 * platform operator") is deemed the supplier and must collect GST/HST on
 * goods sold by NON-REGISTERED vendors only when the goods are located in
 * Canada or shipped from a place in Canada. Goods shipped from outside Canada
 * are excluded; the GST/HST (and PST/QST under CBSA agreements) is collected
 * at import from the buyer by the courier (CRA "Supply of qualifying goods in
 * Canada", Example 3; CBSA D17-4-0, D2-3-6). A US seller shipping to a
 * Canadian buyer therefore owes nothing at checkout; only a Canadian seller's
 * shipment does. The provincial marketplace rules follow the same line for BC
 * and QC, but Saskatchewan and Manitoba say they reach goods shipped from
 * outside Canada too (see `provincialMarketplace.reachesImports`).
 *
 * Exemptions: basic groceries and feminine hygiene products are zero-rated
 * federally; HST provinces rebate the provincial portion at the point of sale
 * on printed books (ON, NS, NB, PE) and children's clothing (ON, NS, PE);
 * the PST provinces exempt groceries, children's clothing and books outright.
 * Motor vehicles are taxed at registration by the province, not at the sale.
 */

export const CA_PROVINCE_CODES = ['AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'NT', 'NU', 'ON', 'PE', 'QC', 'SK', 'YT'] as const;
export const CaProvinceCodeSchema = z.enum(CA_PROVINCE_CODES);
export type CaProvinceCode = z.infer<typeof CaProvinceCodeSchema>;

/** How a product class is treated by one layer (federal or provincial) of Canadian tax. */
export const CaTreatmentSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('taxable') }),
  /** Zero-rated or exempt under this layer. */
  z.object({ kind: z.literal('exempt'), note: z.string().optional() }),
  /** HST provinces only: the provincial portion is rebated at the point of sale, the 5% federal portion still applies. */
  z.object({ kind: z.literal('pos_rebate'), note: z.string().optional() }),
  /** Exempt when the unit price is at or below a threshold, otherwise fully taxable (MB children's clothing). */
  z.object({ kind: z.literal('exempt_at_or_below'), thresholdCents: z.number().int().positive(), note: z.string().optional() }),
  z.object({ kind: z.literal('taxed_at_registration') }),
]);
export type CaTreatment = z.infer<typeof CaTreatmentSchema>;

export const CaProvinceTaxRuleSchema = z.object({
  code: CaProvinceCodeSchema,
  name: z.string(),
  federal: z.object({
    type: z.enum(['GST', 'HST']),
    ratePercent: z.number().min(0),
    /** For HST, the provincial share of the harmonised rate (the part a POS rebate removes). */
    provincialPortionPercent: z.number().min(0).default(0),
  }),
  provincial: z
    .object({
      type: z.enum(['PST', 'RST', 'QST']),
      ratePercent: z.number().min(0),
    })
    .nullable(),
  combinedRatePercent: z.number().min(0),
  /** Provincial marketplace-facilitator (online platform) rules for goods delivered into the province. */
  provincialMarketplace: z
    .object({
      effective: z.string(),
      thresholdCad: z.number().int().min(0).nullable(),
      /** True when the province says the platform collects even on goods shipped from outside Canada. */
      reachesImports: z.boolean(),
      note: z.string(),
    })
    .nullable(),
  federalExemptions: z.partialRecord(ProductTaxClassSchema, CaTreatmentSchema).default({}),
  provincialExemptions: z.partialRecord(ProductTaxClassSchema, CaTreatmentSchema).default({}),
  notes: z.array(z.string()).default([]),
  verify: z.array(z.string()).default([]),
});
export type CaProvinceTaxRule = z.infer<typeof CaProvinceTaxRuleSchema>;
type CaProvinceTaxRuleInput = z.input<typeof CaProvinceTaxRuleSchema>;

const EXEMPT = { kind: 'exempt' } as const;
const DMV = { kind: 'taxed_at_registration' } as const;

/** Zero-rated federally in every province: basic groceries; feminine hygiene products (since 2015-07-01). */
const FEDERAL_BASE = { groceries: EXEMPT, motor_vehicle: DMV } as const;

const RAW_RULES: CaProvinceTaxRuleInput[] = [
  {
    code: 'AB', name: 'Alberta', federal: { type: 'GST', ratePercent: 5 }, provincial: null, combinedRatePercent: 5,
    provincialMarketplace: null, federalExemptions: FEDERAL_BASE,
  },
  {
    code: 'BC', name: 'British Columbia', federal: { type: 'GST', ratePercent: 5 }, provincial: { type: 'PST', ratePercent: 7 }, combinedRatePercent: 12,
    provincialMarketplace: {
      effective: '2022-07-01', thresholdCad: 10_000, reachesImports: false,
      note: 'Bulletin PST 142: a marketplace facilitator (inside or outside Canada) that facilitates sellers\' retail sales and collects payment must register once facilitated retail sales exceed CAD 10,000 in 12 months. Covers goods located in Canada at the time of sale; goods brought in from outside Canada are taxed by CBSA/Canada Post instead. Online marketplace services billed to BC sellers are taxed at 7%.',
    },
    federalExemptions: FEDERAL_BASE,
    provincialExemptions: { groceries: EXEMPT, childrens_clothing: { kind: 'exempt', note: 'Children-sized clothing, or clothing for a child under 15 with documentation.' }, books: EXEMPT, motor_vehicle: DMV },
    verify: ['PST treatment of private used-goods sales other than vehicles, boats and aircraft.'],
  },
  {
    code: 'MB', name: 'Manitoba', federal: { type: 'GST', ratePercent: 5 }, provincial: { type: 'RST', ratePercent: 7 }, combinedRatePercent: 12,
    provincialMarketplace: {
      effective: '2021-12-01', thresholdCad: 30_000, reachesImports: true,
      note: 'Bulletin RST 064: an online sales platform that collects payment on behalf of sellers collects RST on every taxable sale through it, whether or not the seller is registered and even when goods ship to Manitoba from outside Canada. Delivery charges taxable unless pickup is possible. The CAD 30,000 small-business exception (RST 24-01) is not confirmed to reach platform operators.',
    },
    federalExemptions: FEDERAL_BASE,
    provincialExemptions: { groceries: EXEMPT, childrens_clothing: { kind: 'exempt_at_or_below', thresholdCents: 15_000, note: 'Children\'s clothing and footwear up to CAD 150.' }, books: EXEMPT, motor_vehicle: DMV },
    verify: ['Whether the CAD 30,000 registration exception applies to platform operators.', 'The CAD 150 children\'s clothing cap against RST 001.'],
  },
  {
    code: 'NB', name: 'New Brunswick', federal: { type: 'HST', ratePercent: 15, provincialPortionPercent: 10 }, provincial: null, combinedRatePercent: 15,
    provincialMarketplace: null,
    federalExemptions: { ...FEDERAL_BASE, books: { kind: 'pos_rebate', note: 'Printed books: provincial portion rebated at the point of sale (GI-065).' } },
  },
  {
    code: 'NL', name: 'Newfoundland and Labrador', federal: { type: 'HST', ratePercent: 15, provincialPortionPercent: 10 }, provincial: null, combinedRatePercent: 15,
    provincialMarketplace: null,
    federalExemptions: { ...FEDERAL_BASE, books: { kind: 'pos_rebate', note: 'Printed books: rebate removed 2017-01-01 and reinstated 2018-01-01 (NL Finance, "Reinstatement of the General HST Point-of-Sale Book Rebate").' } },
    notes: ['The 2026-09-11 comparison run caught the first draft of this row, which still had the 2017 repeal; Stripe applied the rebate and was right.'],
  },
  {
    code: 'NS', name: 'Nova Scotia', federal: { type: 'HST', ratePercent: 14, provincialPortionPercent: 9 }, provincial: null, combinedRatePercent: 14,
    provincialMarketplace: null,
    federalExemptions: {
      ...FEDERAL_BASE,
      books: { kind: 'pos_rebate', note: 'Printed books (GI-065).' },
      childrens_clothing: { kind: 'pos_rebate', note: 'Children\'s clothing and footwear (GI-063); sports-only items excluded.' },
    },
    notes: ['HST cut from 15% to 14% on 2025-04-01 (provincial portion 10% to 9%).', 'Children\'s diapers also rebated.'],
  },
  {
    code: 'NT', name: 'Northwest Territories', federal: { type: 'GST', ratePercent: 5 }, provincial: null, combinedRatePercent: 5,
    provincialMarketplace: null, federalExemptions: FEDERAL_BASE,
  },
  {
    code: 'NU', name: 'Nunavut', federal: { type: 'GST', ratePercent: 5 }, provincial: null, combinedRatePercent: 5,
    provincialMarketplace: null, federalExemptions: FEDERAL_BASE,
  },
  {
    code: 'ON', name: 'Ontario', federal: { type: 'HST', ratePercent: 13, provincialPortionPercent: 8 }, provincial: null, combinedRatePercent: 13,
    provincialMarketplace: null,
    federalExemptions: {
      ...FEDERAL_BASE,
      books: { kind: 'pos_rebate', note: 'Printed books, audiobooks and scriptures.' },
      childrens_clothing: { kind: 'pos_rebate', note: 'Up to girls\' size 16 / boys\' size 20; footwear insole up to 24.25 cm; sports-only items excluded.' },
    },
    notes: ['Point-of-sale rebates also cover children\'s diapers, car seats, newspapers and prepared food under $4.'],
  },
  {
    code: 'PE', name: 'Prince Edward Island', federal: { type: 'HST', ratePercent: 15, provincialPortionPercent: 10 }, provincial: null, combinedRatePercent: 15,
    provincialMarketplace: null,
    federalExemptions: {
      ...FEDERAL_BASE,
      books: { kind: 'pos_rebate', note: 'Printed books (GI-065).' },
      childrens_clothing: { kind: 'pos_rebate', note: 'Children\'s clothing and footwear (GI-063).' },
    },
  },
  {
    code: 'QC', name: 'Quebec', federal: { type: 'GST', ratePercent: 5 }, provincial: { type: 'QST', ratePercent: 9.975 }, combinedRatePercent: 14.975,
    provincialMarketplace: {
      effective: '2019-01-01', thresholdCad: 30_000, reachesImports: false,
      note: 'Operators of distribution platforms outside Quebec register under the specified QST system once CAD 30,000 is exceeded. Goods count only when delivered in Quebec by a supplier outside Quebec not registered under the general system, and goods shipped from outside Canada by mail or courier are excluded.',
    },
    federalExemptions: FEDERAL_BASE,
    provincialExemptions: { groceries: EXEMPT, books: { kind: 'exempt', note: 'Printed books with an ISBN are zero-rated for QST; the 5% GST still applies.' }, motor_vehicle: DMV },
    notes: ['Baby diapers, training pants and breastfeeding items are QST zero-rated.', 'From 2026-07-15 select snack foods, toilet paper and facial tissue are QST-free at grocery-type retailers.'],
    verify: ['QST treatment of children\'s clothing (not listed on Revenu Quebec\'s children\'s products page; treated as taxable).'],
  },
  {
    code: 'SK', name: 'Saskatchewan', federal: { type: 'GST', ratePercent: 5 }, provincial: { type: 'PST', ratePercent: 6 }, combinedRatePercent: 11,
    provincialMarketplace: {
      effective: '2020-01-01', thresholdCad: null, reachesImports: true,
      note: 'IN 2020-08: a marketplace facilitator that collects payment must be licensed even if it does not carry on business in Saskatchewan, with no threshold, and collects on all taxable sales through the platform regardless of the seller\'s licence status, including goods delivered into Saskatchewan from outside the province. No carve-out for goods shipped from outside Canada is stated.',
    },
    federalExemptions: FEDERAL_BASE,
    provincialExemptions: { groceries: EXEMPT, childrens_clothing: EXEMPT, books: { kind: 'exempt', note: 'Printed books, newspapers, magazines; e-books and audiobooks since 2022-04-01.' }, motor_vehicle: DMV },
    verify: ['Whether Saskatchewan applies a CBSA carve-out for goods shipped from outside Canada.', 'PST treatment of private used-goods sales.'],
  },
  {
    code: 'YT', name: 'Yukon', federal: { type: 'GST', ratePercent: 5 }, provincial: null, combinedRatePercent: 5,
    provincialMarketplace: null, federalExemptions: FEDERAL_BASE,
  },
];

export const CA_PROVINCE_TAX_RULES: Readonly<Record<CaProvinceCode, CaProvinceTaxRule>> = Object.fromEntries(
  RAW_RULES.map((rule) => {
    const parsed = CaProvinceTaxRuleSchema.parse(rule);
    return [parsed.code, parsed];
  }),
) as Record<CaProvinceCode, CaProvinceTaxRule>;

export const CA_TABLE_SOURCE = {
  rates: 'CRA, GST/HST rates and place-of-supply rules (modified 2026-07-15); provincial bulletins PST 142, IN 2020-08, RST 064, Revenu Quebec specified-system page',
  federalPlatformRule: 'CRA, Digital economy: supply of qualifying goods in Canada (effective 2021-07-01, CAD 30,000 threshold over 12 months)',
  importRule: 'CBSA D17-4-0 (CLVS), D2-3-6, CUSMA de minimis: CAD 40 tax-free, CAD 150 duty-free by courier from the US',
  compiledOn: '2026-09-11',
} as const;

/** Federal distribution-platform registration threshold, CAD over any 12 months. */
export const CA_FEDERAL_PLATFORM_THRESHOLD_CAD = 30_000;

export function getCaProvinceTaxRule(code: string): CaProvinceTaxRule | null {
  const parsed = CaProvinceCodeSchema.safeParse(code.trim().toUpperCase());
  return parsed.success ? CA_PROVINCE_TAX_RULES[parsed.data] : null;
}
